/**
 * 入住时间模块
 */
import React, { memo, useMemo } from 'react';
import { connect } from 'react-redux';
import { Select } from 'antd';
import intl from 'react-intl-universal';

import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import NumberWithHelper from '@/components/Reservation/NumberInput/InputWithHelper';
import DateJS, { format2DateStr, format2FullTimeStr, format2TimeStr } from '@/utils/date';
import {
  OccType,
  DayDepartureOptions,
  OCC_TYPE_MAP,
  OCC_VALUE_MAP,
  PriceChannel,
} from '@/config/constant';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { handleRentTimeChange } from '@/redux/actions/reservation/checkInEditor';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkInEditor';
import { formatDayOutTime, formatHourOutTime } from '@/pages/reservation/utils/formatter';

import { getPopupContainer } from '../../helper';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentTimeInfo, commonInfo, originBaseInfo } = state;
  return {
    // 直连渠道 或 编辑的是最后一条续住记录 不可编辑
    // v1.35 去除直连渠道限制
    disabled: originBaseInfo.renewalId && !originBaseInfo.isLatestRecord,
    auditPassedCount: commonInfo.auditPassedCount,
    rentTimeInfo,
    priceChannel: originBaseInfo.priceChannel,
    originOccValue: originBaseInfo.occDuration,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callRentTimeChange: handleRentTimeChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

/**
 * 直连渠道 入住时长不可修改 日租房 最小值为1+已过夜审
 * 直连渠道 日租房预离时间不可修改
 */

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    rentTimeInfo,
    disabled,
    auditPassedCount,
    originOccValue,
    priceChannel,
    callRentTimeChange,
  } = props;
  const { occType, expectedArrivalTime, expectedDepartureTime, rentTime } = rentTimeInfo;
  const isDayType = occType === OccType.DayUse;

  // 日租房入住时长范围
  const { minOccValue, maxOccValue } = useMemo(() => {
    return {
      // 已过夜审的日期数，如果没经历过夜审则取1
      minOccValue: auditPassedCount || 1,
      // 直连渠道取原始入住时长 非直连最大180
      // V2.12 CRS取原始入住时长
      maxOccValue: [PriceChannel.CRS].includes(priceChannel) ? originOccValue : 180,
    };
  }, [auditPassedCount, originOccValue, priceChannel]);

  // 日租房入住时间调整
  const handleDayDurationChange: (value: string) => void = (value) => {
    if (disabled) return; // 不可修改
    let result: number;
    if (value === null) {
      result = minOccValue;
    } else {
      if (!/^\d*$/.test(value)) return;
      if (value) {
        result = Number(value);
        // 日租房 入住时长范围
        if (result < minOccValue || result > maxOccValue) return;
      }
    }
    callRentTimeChange({ rentTime: result });
  };

  // 日租房预离时间调整
  const handleDayDepartureChange: (departureTime: string) => void = (departureTime) => {
    if (disabled) return; // 不可修改
    callRentTimeChange({
      expectedDepartureTime: DateJS(`${format2DateStr(expectedDepartureTime)} ${departureTime}`),
    });
  };

  // 钟点房入住时长修改【不可修改】
  const handleHourDurationChange: (value: string) => void = () => {
    // if (isDirectChannel) return; // 直连渠道不可修改
    // let result: number;
    // if (value === null) {
    //   result = 2;
    // } else {
    //   if (!/^\d*$/.test(value)) return;
    //   if (value) {
    //     result = Number(value);
    //     // 钟点房 2-6 小时
    //     if (result < 2 || result > 6) return;
    //   }
    // }
    // callRentTimeChange({ rentTime: result });
  };

  return (
    <ContentBlock
      title={intl.get('drawer_checkin_time_block_title')}
      className={styles.timeInfoContainer}
    >
      <FormItem label={intl.get('app_reservation_occ_type')} className={styles.occTypeItem}>
        {OCC_TYPE_MAP[occType]}
      </FormItem>
      <br />
      <FormItem label={intl.get('app_reservation_checkin_time')}>
        {format2FullTimeStr(expectedArrivalTime)}
      </FormItem>
      {isDayType ? (
        <>
          <FormItem
            label={intl.get('app_reservation_occ_duration')}
            className={styles.rentTimeItem}
          >
            <NumberWithHelper
              disabled={disabled}
              value={rentTime}
              onChange={handleDayDurationChange}
            />
            <span className={styles.rentTimeUnit}>{OCC_VALUE_MAP[OccType.DayUse]}</span>
          </FormItem>
          <FormItem label={intl.get('app_reservation_etd_time')} className={styles.dayPreOutItem}>
            <span className={styles.outDateWrapper}>
              {formatDayOutTime(
                expectedArrivalTime && rentTime ? expectedDepartureTime : undefined,
              )}
            </span>
            <Select
              placeholder={intl.get('reservation_field_select_default_placeholder')}
              disabled={disabled}
              options={DayDepartureOptions}
              value={expectedDepartureTime ? format2TimeStr(expectedDepartureTime) : undefined}
              onChange={handleDayDepartureChange}
              getPopupContainer={getPopupContainer}
            />
          </FormItem>
        </>
      ) : (
        <>
          <FormItem
            label={intl.get('app_reservation_occ_duration')}
            className={styles.rentTimeItem}
          >
            <NumberWithHelper disabled value={rentTime} onChange={handleHourDurationChange} />
            <span className={styles.rentTimeUnit}>{OCC_VALUE_MAP[OccType.TimeRENT]}</span>
          </FormItem>
          <FormItem label={intl.get('app_reservation_etd_time')} className={styles.hourPreOutItem}>
            <span className={styles.outDateWrapper}>
              {formatHourOutTime(
                expectedArrivalTime && rentTime ? expectedDepartureTime : undefined,
              )}
            </span>
          </FormItem>
        </>
      )}
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
